import { useEffect, useState } from 'react';

/** Jak długo przycisk „Kopiuj” pokazuje potwierdzenie po skopiowaniu. */
const COPIED_FEEDBACK_MS = 2000;

interface ShareLinkProps {
  readonly url: string;
  /** Tekst wiadomości — link jest do niego doklejany dla WhatsApp/SMS. */
  readonly message: string;
  /** Etykieta dostępności pola z adresem. */
  readonly inputLabel: string;
}

/**
 * Wysłanie linku bez przepisywania czegokolwiek: systemowe „Udostępnij” (na telefonie od razu
 * lista komunikatorów), a obok WhatsApp/SMS i kopiowanie jako droga awaryjna na komputerze
 * albo gdy `navigator.share` jest niedostępne.
 */
export function ShareLink({ url, message, inputLabel }: ShareLinkProps) {
  const fullMessage = `${message} ${url}`;
  // iOS wymaga `sms:&body=`, Android (i reszta) `sms:?body=` — jedyna różnica między nimi.
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const canShare = typeof navigator.share === 'function';
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function shareNative() {
    try {
      await navigator.share({ text: message, url });
    } catch {
      // AbortError przy anulowaniu przez użytkownika albo brak wsparcia mimo detekcji —
      // w obu przypadkach nie ma czego pokazywać, pozostałe przyciski zostają jako droga awaryjna.
    }
  }

  async function copy() {
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
    } catch {
      // Brak uprawnień do schowka — pole z adresem obok da się zaznaczyć ręcznie.
    }
  }

  return (
    <>
      <div className="invite__share">
        {canShare && (
          <button type="button" className="btn btn--primary" onClick={() => void shareNative()}>
            Wyślij zaproszenie…
          </button>
        )}
        <a
          className="btn btn--ghost btn--small"
          href={`https://wa.me/?text=${encodeURIComponent(fullMessage)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          WhatsApp
        </a>
        <a
          className="btn btn--ghost btn--small"
          href={`sms:${isIos ? '&' : '?'}body=${encodeURIComponent(fullMessage)}`}
        >
          SMS
        </a>
      </div>
      <p className="invite__link">
        <input
          readOnly
          value={url}
          onFocus={(event) => event.target.select()}
          aria-label={inputLabel}
        />
        <button type="button" className="btn" onClick={() => void copy()}>
          {copied ? 'Skopiowano ✓' : 'Kopiuj'}
        </button>
      </p>
    </>
  );
}
